import type { Database, Row, SqlValue, Tx } from './sqlite';

/**
 * Выгрузка и загрузка содержимого базы — то, из чего собирается архив дел
 * ([D-47](docs/DECISIONS.md), шаг 19 [WEB_ROADMAP.md](docs/WEB_ROADMAP.md)).
 *
 * SQL живёт здесь, а не в `core/archive`: разговаривать с базой умеет один модуль,
 * и правило это не для красоты — запрос, разъехавшийся с репозиторием, находится
 * последним.
 *
 * **Что попадает в архив.** Только живые записи. Мягко удалённые дела и документы
 * ждут в базе своей уборки ([D-12](docs/DECISIONS.md)), и переносить их на другой
 * телефон — значит воскрешать удалённое.
 */

/** Таблицы в порядке, в котором их можно вставлять: родители раньше детей. */
export const ARCHIVE_TABLES = [
  'clients',
  'cases',
  'documents',
  'events',
  'tags',
  'case_tags',
] as const;

export type ArchiveTable = (typeof ARCHIVE_TABLES)[number];

/** Содержимое базы построчно: таблица → строки как есть. */
export type TableRows = Partial<Record<ArchiveTable, Row[]>>;

/** Сколько записей архив принёс на устройство. */
export interface RestoredCounts {
  clients: number;
  cases: number;
  documents: number;
  events: number;
}

/*
 * Запросы выгрузки.
 *
 * Ребёнок удалённого родителя не выгружается вовсе: документ дела, которого больше
 * нет, — это не документ, а мусор. А вот ссылка на удалённого клиента только гасится
 * при загрузке: само дело остаётся делом.
 */
const LIVE_CASES = 'SELECT id FROM cases WHERE deletedAt IS NULL';

const DUMP: Record<ArchiveTable, string> = {
  clients: 'SELECT * FROM clients WHERE deletedAt IS NULL',
  cases: 'SELECT * FROM cases WHERE deletedAt IS NULL',
  documents: `SELECT * FROM documents
               WHERE deletedAt IS NULL AND (caseId IS NULL OR caseId IN (${LIVE_CASES}))`,
  events: `SELECT * FROM events
            WHERE deletedAt IS NULL AND (caseId IS NULL OR caseId IN (${LIVE_CASES}))`,
  tags: 'SELECT * FROM tags',
  case_tags: `SELECT * FROM case_tags WHERE caseId IN (${LIVE_CASES})`,
};

/** Первичный ключ таблицы — по нему разрешается столкновение при загрузке. */
const PRIMARY_KEY: Record<ArchiveTable, string[]> = {
  clients: ['id'],
  cases: ['id'],
  documents: ['id'],
  events: ['id'],
  tags: ['id'],
  case_tags: ['caseId', 'tagId'],
};

/** Ссылки, которые гасятся, если цели нет: колонка → таблица, куда она смотрит. */
const OPTIONAL_LINKS: Partial<Record<ArchiveTable, Record<string, ArchiveTable>>> = {
  cases: { clientId: 'clients' },
  documents: { clientId: 'clients' },
  events: { clientId: 'clients' },
};

/** Ссылки, без которых строка бессмысленна: её не загружаем вовсе. */
const REQUIRED_LINKS: Partial<Record<ArchiveTable, Record<string, ArchiveTable>>> = {
  documents: { caseId: 'cases' },
  events: { caseId: 'cases' },
  case_tags: { caseId: 'cases', tagId: 'tags' },
};

/** Значение из JSON — в то, что понимает SQLite. Всё прочее считается пустым. */
function cell(value: unknown): SqlValue {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string') return value;

  return null;
}

export function archiveRepository(db: Database) {
  /**
   * Колонки таблицы спрашиваем у самой базы: второй список схемы разошёлся бы с ней.
   *
   * Вход [Tx] обязателен: внутри транзакции запрос «снаружи» встанет в общую очередь
   * за этой же транзакцией и будет ждать её до бесконечности — `sqlite.ts`.
   */
  const columnsOf = async (tx: Tx, table: ArchiveTable): Promise<string[]> => {
    const rows = await tx.query<{ name: string }>(`PRAGMA table_info(${table})`);
    return rows.map((row) => row.name);
  };

  return {
    /** Всё живое содержимое базы построчно. */
    async dump(): Promise<TableRows> {
      const tables: TableRows = {};

      for (const table of ARCHIVE_TABLES) {
        tables[table] = await db.query<Row>(DUMP[table]);
      }
      return tables;
    },

    /**
     * Загружает содержимое архива в базу.
     *
     * **Архив ничего не стирает.** Записи сливаются по идентификатору, и при
     * столкновении побеждает та, что новее по `updatedAt`: юрист восстанавливает
     * данные на телефон, где уже работал, и вчерашняя копия не имеет права затирать
     * сегодняшнюю правку. Повторная загрузка того же архива не меняет ничего.
     *
     * **Почему не `INSERT OR REPLACE`.** Он удаляет прежнюю строку, а удаление дела
     * по внешнему ключу уносит каскадом все его документы. Загрузка архива поверх
     * существующего дела стёрла бы то, чего в архиве нет.
     */
    async restore(tables: TableRows): Promise<RestoredCounts> {
      const counts: RestoredCounts = { clients: 0, cases: 0, documents: 0, events: 0 };

      await db.transaction(async (tx) => {
        /** Идентификаторы, уже лежащие в базе, — вместе с только что вставленными. */
        const known = new Map<ArchiveTable, Set<string>>();
        const idsOf = async (table: ArchiveTable): Promise<Set<string>> => {
          const existing = known.get(table);
          if (existing) return existing;

          const rows = await tx.query<{ id: string }>(`SELECT id FROM ${table}`);
          const ids = new Set(rows.map((row) => String(row.id)));
          known.set(table, ids);

          return ids;
        };

        /**
         * Одна и та же запись под разными идентификаторами: архивный → здешний.
         *
         * Пока такое бывает только с тегами. «Срочное», заведённое на двух телефонах,
         * — это один тег юриста и два разных идентификатора, а имя тега в схеме
         * уникально. Без сведения загрузка архива падала бы на первом же общем теге;
         * поймано тестом «архив ничего не стирает».
         */
        const aliases = new Map<ArchiveTable, Map<string, string>>();
        const sameAs = (table: ArchiveTable, id: string): string =>
          aliases.get(table)?.get(id) ?? id;

        /** Сравнение имён без учёта регистра — так же, как при заведении тега. */
        const byName = (name: string) => name.trim().toLocaleLowerCase('ru');

        /** Ищет тегам архива их двойников на устройстве. */
        const matchTags = async (rows: Row[]) => {
          const existing = await tx.query<{ id: string; name: string }>('SELECT id, name FROM tags');
          const here = new Map(existing.map((row) => [byName(String(row.name)), String(row.id)]));
          const map = new Map<string, string>();

          for (const row of rows) {
            const id = String(row['id'] ?? '');
            const twin = here.get(byName(String(row['name'] ?? '')));
            if (twin && twin !== id) map.set(id, twin);
          }
          aliases.set('tags', map);
        };

        for (const table of ARCHIVE_TABLES) {
          const rows = tables[table];
          if (!rows || rows.length === 0) continue;

          if (table === 'tags') await matchTags(rows);

          const columns = await columnsOf(tx, table);
          const keys = PRIMARY_KEY[table];
          const optional = OPTIONAL_LINKS[table] ?? {};
          const required = REQUIRED_LINKS[table] ?? {};

          /*
           * Столкновение разрешается по свежести — но только там, где есть чему быть
           * свежим. У тегов и связей дела с тегом нет ни `updatedAt`, ни содержимого:
           * строка либо есть, либо нет.
           */
          const updatable = columns.filter((name) => !keys.includes(name));
          const merge =
            columns.includes('updatedAt') && updatable.length > 0
              ? `DO UPDATE SET ${updatable.map((name) => `${name} = excluded.${name}`).join(', ')}
                 WHERE excluded.updatedAt > ${table}.updatedAt`
              : 'DO NOTHING';

          const sql =
            `INSERT INTO ${table} (${columns.join(', ')}) ` +
            `VALUES (${columns.map(() => '?').join(', ')}) ` +
            `ON CONFLICT(${keys.join(', ')}) ${merge}`;

          const [before] = await tx.query<{ changes: number }>(
            'SELECT total_changes() AS changes',
          );

          for (const row of rows) {
            const values: Record<string, SqlValue> = {};
            for (const name of columns) values[name] = cell(row[name]);

            // Тег, у которого нашёлся здешний двойник, второй раз не заводится.
            if (table === 'tags' && aliases.get('tags')?.has(String(values['id']))) continue;

            let skip = false;
            for (const [column, target] of Object.entries(required)) {
              const id = values[column];
              if (id === null) {
                skip = true;
                continue;
              }

              const here = sameAs(target, String(id));
              if ((await idsOf(target)).has(here)) values[column] = here;
              else skip = true;
            }
            if (skip) continue;

            for (const [column, target] of Object.entries(optional)) {
              const id = values[column];
              if (id === null) continue;

              const here = sameAs(target, String(id));
              values[column] = (await idsOf(target)).has(here) ? here : null;
            }

            await tx.run(
              sql,
              columns.map((name) => values[name] ?? null),
            );

            // Строка, которую только что вставили, — уже цель для чужих ссылок.
            const id = values['id'];
            if (id !== null && id !== undefined) (await idsOf(table)).add(String(id));
          }

          const [after] = await tx.query<{ changes: number }>('SELECT total_changes() AS changes');
          const applied = Number(after?.changes ?? 0) - Number(before?.changes ?? 0);

          if (table in counts) counts[table as keyof RestoredCounts] = applied;
        }
      });

      return counts;
    },
  };
}

export type ArchiveRepository = ReturnType<typeof archiveRepository>;
